import { supabase } from '@/core/database/supabase';
import { enforceSettingsPermission } from '@/modules/settings/permissions/enforceSettingsPermission';
import type { CategoryDetail, LinkedService, CategoryStats } from '../types/categoriesDetail.types';
import type { CategoryType } from '../../list/types/categoriesList.types';

const typeLabelMap: Record<string, string> = {
  work: 'Công việc',
  material: 'Vật tư',
  equipment: 'Thiết bị',
  personnel: 'Nhân sự',
  service: 'Dịch vụ',
  other: 'Khác',
};

export const categoriesDetailService = {
  async getCategoryDetail(id: string): Promise<CategoryDetail | null> {
    await enforceSettingsPermission('settings.categories.view');
    const { data, error } = await supabase
      .from('categories')
      .select(`
        *,
        group:category_groups(code, name),
        parent:categories!parent_id(name)
      `)
      .eq('id', id)
      .maybeSingle();

    if (error) {
      console.error('[CategoriesDetailService] Error fetching detail:', error);
      return null;
    }
    if (!data) return null;

    // Count services
    const { count } = await supabase
      .from('services')
      .select('*', { count: 'exact', head: true })
      .eq('category_id', id);

    let groupCode = (data.group?.code || 'other') as CategoryType;
    
    // Fallback: Determine type from code prefix if group is missing
    if (groupCode === 'other' && data.code) {
      const code = String(data.code).toUpperCase();
      if (code.startsWith('W-')) groupCode = 'work';
      else if (code.startsWith('M-')) groupCode = 'material';
      else if (code.startsWith('E-')) groupCode = 'equipment';
      else if (code.startsWith('P-')) groupCode = 'personnel';
      else if (code.startsWith('S-')) groupCode = 'service';
    }
    
    // Default tones for each category type
    const defaultTones: Record<CategoryType, { name: string; tone: string }> = {
      work: { name: 'ri-briefcase-line', tone: 'bg-blue-100 text-blue-800' },
      material: { name: 'ri-shopping-basket-2-line', tone: 'bg-emerald-100 text-emerald-800' },
      equipment: { name: 'ri-tools-line', tone: 'bg-amber-100 text-amber-800' },
      personnel: { name: 'ri-user-settings-line', tone: 'bg-purple-100 text-purple-800' },
      service: { name: 'ri-customer-service-2-line', tone: 'bg-indigo-100 text-indigo-800' },
      other: { name: 'ri-folder-line', tone: 'bg-slate-200 text-slate-700' },
    };

    const defaultValues = defaultTones[groupCode] || defaultTones.other;
    const iconConfig = data.icon_config || {};

    return {
      id: data.id,
      groupId: data.group_id,
      code: data.code,
      name: data.name,
      description: data.description || '',
      type: groupCode,
      typeName: data.group?.name || typeLabelMap[groupCode] || 'Khác',
      status: data.is_active ? 'active' : 'inactive',
      iconConfig: {
        name: iconConfig.name || defaultValues.name,
        tone: iconConfig.tone || defaultValues.tone,
      },
      parentId: data.parent_id,
      parentName: data.parent?.name,
      path: data.path,
      level: data.level || 0,
      itemCount: count || 0,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  async getSubCategories(parentId: string): Promise<CategoryDetail[]> {
    const { data, error } = await supabase
      .from('categories')
      .select('*, group:category_groups(code, name)')
      .eq('parent_id', parentId)
      .order('display_order', { ascending: true });

    if (error || !data) return [];

    // Default tones for each category type
    const defaultTones: Record<CategoryType, { name: string; tone: string }> = {
      work: { name: 'ri-briefcase-line', tone: 'bg-blue-100 text-blue-800' },
      material: { name: 'ri-shopping-basket-2-line', tone: 'bg-emerald-100 text-emerald-800' },
      equipment: { name: 'ri-tools-line', tone: 'bg-amber-100 text-amber-800' },
      personnel: { name: 'ri-user-settings-line', tone: 'bg-purple-100 text-purple-800' },
      service: { name: 'ri-customer-service-2-line', tone: 'bg-indigo-100 text-indigo-800' },
      other: { name: 'ri-folder-line', tone: 'bg-slate-200 text-slate-700' },
    };

    return data.map(row => {
      let groupCode = (row.group?.code || 'other') as CategoryType;
      
      // Fallback: Determine type from code prefix if group is missing
      if (groupCode === 'other' && row.code) {
        const code = String(row.code).toUpperCase();
        if (code.startsWith('W-')) groupCode = 'work';
        else if (code.startsWith('M-')) groupCode = 'material';
        else if (code.startsWith('E-')) groupCode = 'equipment';
        else if (code.startsWith('P-')) groupCode = 'personnel';
        else if (code.startsWith('S-')) groupCode = 'service';
      }

      const defaultValues = defaultTones[groupCode] || defaultTones.other;
      const iconConfig = row.icon_config || {};

      return {
        id: row.id,
        groupId: row.group_id,
        code: row.code,
        name: row.name,
        description: row.description || '',
        type: groupCode,
        typeName: row.group?.name || typeLabelMap[groupCode] || 'Khác',
        status: row.is_active ? 'active' : 'inactive',
        iconConfig: {
          name: iconConfig.name || defaultValues.name,
          tone: iconConfig.tone || defaultValues.tone,
        },
        parentId: row.parent_id,
        path: row.path,
        level: row.level || 0,
        itemCount: 0,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      };
    });
  },

  async getLinkedServices(categoryId: string): Promise<LinkedService[]> {
    const { data, error } = await supabase
      .from('services')
      .select('id, name, code, is_active, base_price')
      .eq('category_id', categoryId)
      .order('name');

    if (error || !data) return [];

    return data.map(row => ({
      id: row.id,
      name: row.name,
      code: row.code,
      status: row.is_active ? 'active' : 'inactive',
      price: row.base_price || 0,
    }));
  },

  async getCategoryStats(id: string): Promise<CategoryStats[]> {
    // Example: Stats by sub-category item counts or status
    // For now, let's just return some mock stats based on types or status for the donut chart
    const { data } = await supabase.from('categories').select('is_active').eq('parent_id', id);
    const active = data?.filter(d => d.is_active).length || 0;
    const inactive = data?.filter(d => !d.is_active).length || 0;

    return [
      { label: 'Hoạt động', value: active, color: '#10b981' },
      { label: 'Tạm dừng', value: inactive, color: '#94a3b8' },
    ];
  }
};
